import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createProductCategoryApi,
  createPromotedProductApi,
  deleteProductCategoryApi,
  deleteProductImageApi,
  deletePromotedProductApi,
  fetchPendingProducts,
  fetchProductCategories,
  fetchPromotedProduct,
  fetchPromotedProducts,
  invalidatePromotedProductApi,
  setProductImageCoverApi,
  updateProductCategoryApi,
  updatePromotedProductApi,
  updatePromotedProductStockApi,
  uploadProductImageApi,
  validatePromotedProductApi,
} from "../services/promotedProductsService";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

const PRODUCTS_QUERY_KEY = ["promoted-products"];
const PENDING_QUERY_KEY = ["promoted-products", "pending"];
const CATEGORIES_QUERY_KEY = ["product-categories"];

export const usePromotedProducts = (filters) =>
  useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, filters],
    queryFn: () => fetchPromotedProducts(filters),
    keepPreviousData: true,
  });

export const usePendingProducts = (filters) =>
  useQuery({
    queryKey: [...PENDING_QUERY_KEY, filters],
    queryFn: () => fetchPendingProducts(filters),
    keepPreviousData: true,
  });

export const usePromotedProduct = (id, options = {}) =>
  useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, id],
    queryFn: () => fetchPromotedProduct(id),
    enabled: Boolean(id),
    ...options,
  });

export const useProductCategories = () =>
  useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: fetchProductCategories,
  });

/** Invalidates every product-related list plus one product's detail. */
const useInvalidateProducts = () => {
  const queryClient = useQueryClient();
  return (id) => {
    queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: PENDING_QUERY_KEY });
    if (id) {
      queryClient.invalidateQueries({ queryKey: [...PRODUCTS_QUERY_KEY, id] });
    }
  };
};

export const useCreatePromotedProduct = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: createPromotedProductApi,
    onSuccess: () => invalidate(),
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Produit non ajouté"), {
        autoClose: 4000,
      });
    },
  });
};

export const useUpdatePromotedProduct = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ id, payload }) => updatePromotedProductApi(id, payload),
    onSuccess: (_data, variables) => invalidate(variables?.id),
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Produit non modifié"), {
        autoClose: 4000,
      });
    },
  });
};

export const useDeletePromotedProduct = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: (id) => deletePromotedProductApi(id),
    onSuccess: () => {
      invalidate();
      toast.success("Produit supprimé avec succès", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(
        resolveApiErrorMessage(
          err,
          "Une erreur est survenue lors de la suppression du produit"
        ),
        { autoClose: 4000 }
      );
    },
  });
};

export const useValidatePromotedProduct = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: (id) => validatePromotedProductApi(id),
    onSuccess: (_data, id) => {
      invalidate(id);
      toast.success("Produit validé — il est maintenant visible sur mobile", {
        autoClose: 3000,
      });
    },
    onError: (err) => {
      // A 400 here means "no image yet": the API message says so explicitly.
      toast.error(resolveApiErrorMessage(err, "Produit non validé"), {
        autoClose: 5000,
      });
    },
  });
};

export const useInvalidatePromotedProduct = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ id, rejectionReason }) =>
      invalidatePromotedProductApi(id, rejectionReason),
    onSuccess: (_data, variables) => {
      invalidate(variables?.id);
      toast.success("Produit retiré de l'application mobile", {
        autoClose: 3000,
      });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Produit non invalidé"), {
        autoClose: 4000,
      });
    },
  });
};

export const useUpdateProductStock = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ id, stockQuantity }) =>
      updatePromotedProductStockApi(id, stockQuantity),
    onSuccess: (_data, variables) => {
      invalidate(variables?.id);
      toast.success("Stock mis à jour", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Stock non mis à jour"), {
        autoClose: 4000,
      });
    },
  });
};

export const useUploadProductImage = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ productId, file, isCover, position, onUploadProgress }) =>
      uploadProductImageApi(productId, {
        file,
        isCover,
        position,
        onUploadProgress,
      }),
    onSuccess: (_data, variables) => invalidate(variables?.productId),
  });
};

export const useDeleteProductImage = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ imageId }) => deleteProductImageApi(imageId),
    onSuccess: (_data, variables) => invalidate(variables?.productId),
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Image non supprimée"), {
        autoClose: 4000,
      });
    },
  });
};

export const useSetProductImageCover = () => {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ imageId }) => setProductImageCoverApi(imageId),
    onSuccess: (_data, variables) => invalidate(variables?.productId),
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Couverture non définie"), {
        autoClose: 4000,
      });
    },
  });
};

export const useCreateProductCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (designation) => createProductCategoryApi(designation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
      toast.success("Catégorie créée", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Catégorie non créée"), {
        autoClose: 4000,
      });
    },
  });
};

export const useUpdateProductCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, designation }) => updateProductCategoryApi(id, designation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
      toast.success("Catégorie modifiée", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Catégorie non modifiée"), {
        autoClose: 4000,
      });
    },
  });
};

export const useDeleteProductCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteProductCategoryApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
      toast.success("Catégorie supprimée", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Catégorie non supprimée"), {
        autoClose: 4000,
      });
    },
  });
};
